import React, { useEffect, useState } from 'react'
import {
  Form,
  Input,
  Button,
  Row,
  Col,
  Select,
  DatePicker,
  Space,
  Table,
  Divider,
  Modal,
  Spin,
} from 'antd'
import dayjs from 'dayjs'
import AdminHeader from '../CommonComponents/AdminHeader'
import AdminFooter from '../CommonComponents/AdminFooter'
import { toast } from 'react-toastify'
import {
  getChildrenList,
  getBuilding,
  getFloorById,
  saveRoomAllocation,
  GetRoomAllocation,
  updateRoomAllocation,
  DeleteRoomAllocation,
} from '../APIComponents/AdminApi'
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
} from '@ant-design/icons'
import axiosInstance from '../../Utils/axiosInstance'

const { Option } = Select

const RoomAllocationForm = () => {
  //===============================================
  const loginType = sessionStorage.getItem('loginType') // 'ADMIN' or 'USER'
  const menuData = JSON.parse(sessionStorage.getItem('menus')) || []

  const getPagePermission = () => {
    if (loginType === 'ADMIN') {
      return {
        is_new: 1,
        is_edit: 1,
        is_view: 1,
        is_delete: 1,
      }
    }

    for (const menu of menuData) {
      const page = menu.pages?.find((p) => p.page_name === 'Room Allocation')
      if (page) return page
    }

    return {
      is_new: 0,
      is_edit: 0,
      is_view: 0,
      is_delete: 0,
    }
  }

  const pagePermission = getPagePermission()
  const showActionColumn =
    loginType === 'ADMIN' ||
    pagePermission.is_edit == 1 ||
    pagePermission.is_view == 1 ||
    pagePermission.is_delete == 1
  //============================================

  const [form] = Form.useForm()

  const [childrenList, setChildrenList] = useState([])
  const [buildingList, setBuildingList] = useState([])
  const [wingList, setWingList] = useState([])
  const [allocationList, setAllocationList] = useState([])

  const [loading, setLoading] = useState(false)
  const [wingDisabled, setWingDisabled] = useState(true)
  const [editLoading, setEditLoading] = useState(false)
  const [tableLoading, setTableLoading] = useState(false)

  const [showForm, setShowForm] = useState(false)
  const [editId, setEditId] = useState(null)
  const [searchText, setSearchText] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [deleteModal, setDeleteModal] = useState(false)
  const [deleteId, setDeleteId] = useState(null)
  const [viewModalVisible, setViewModalVisible] = useState(false)
  const [viewData, setViewData] = useState(null)

  const handleView = (record) => {
    setViewData(record)
    setViewModalVisible(true)
  }

  useEffect(() => {
    fetchChildren()
    fetchBuildings()
    fetchAllocations()
  }, [])

  const fetchAllocations = async () => {
    try {
      setTableLoading(true)
      const res = await axiosInstance.get(GetRoomAllocation)
      setAllocationList(res.data)
    } catch {
      toast.error('Failed to fetch allocations')
    } finally {
      setTableLoading(false)
    }
  }

  const fetchChildren = async () => {
    const res = await axiosInstance.get(getChildrenList)
    setChildrenList(res.data)
  }

  const fetchBuildings = async () => {
    const res = await axiosInstance.get(getBuilding)
    setBuildingList(res.data)
  }

  const fetchWings = async (buildingId) => {
    try {
      setWingDisabled(true)
      const res = await axiosInstance.get(`${getFloorById}/${buildingId}`)
      const wings = Array.isArray(res.data) ? res.data : [res.data]
      setWingList(wings)
      setWingDisabled(false)
    } catch {
      setWingList([])
      toast.error('Failed to fetch wings')
    }
  }

  const handleBuildingChange = (buildingId) => {
    form.setFieldsValue({ wingId: undefined })
    fetchWings(buildingId)
  }

  // =============================
  // ADD BUTTON CLICK
  // =============================
  const handleAdd = () => {
    setShowForm(true)
    setEditId(null)
    form.resetFields()
    form.setFieldsValue({
      allocationDate: dayjs(),
    })
  }

  // =============================
  // EDIT BUTTON CLICK
  // =============================
  const handleEdit = async (record) => {
    try {
      setEditLoading(true)
      setShowForm(true)
      setEditId(record.id)

      await fetchWings(record.building_id)

      form.setFieldsValue({
        childId: record.reg_id,
        allocationDate: dayjs(record.allocation_date),
        buildingId: record.building_id,
        wingId: record.wing_id,
      })
    } catch (error) {
      console.error(error)
    } finally {
      setEditLoading(false) //  Stop Loader
    }
  }

  // ================= DELETE =================
  const handleDelete = async () => {
    try {
      const res = await axiosInstance.delete(
        `${DeleteRoomAllocation}/${deleteId}`,
      )
      if (res.data === 'DELETED') {
        toast.success('Deleted successfully')
        setDeleteModal(false)
        fetchAllocations()
      } else if (res.data === 'NOTFOUND') {
        toast.warn('Data not found!')
      }
    } catch (err) {
      toast.error('Delete failed')
    }
  }

  // =============================
  // SUBMIT / UPDATE
  // =============================
  const handleFinish = async (values) => {
    try {
      setLoading(true)

      const objData = {
        reg_id: values.childId,
        allocation_date: values.allocationDate.format('YYYY-MM-DD'),
        building_id: values.buildingId,
        wing_id: values.wingId,
      }

      if (editId) {
        // UPDATE
        const res = await axiosInstance.put(
          `${updateRoomAllocation}/${editId}`,
          objData,
        )
        if (res.data == 'ALREADY_EXIST') {
          toast.warn('Room already allocated!')
        } else if (res.data == 'UPDATED') {
          toast.success('Room updated successfully')
          form.resetFields()
          setShowForm(false)
          setEditId(null)
          fetchAllocations()
        }
      } else {
        // SAVE
        const res = await axiosInstance.post(saveRoomAllocation, objData)
        if (res.data == 'ALREADY_EXIST') {
          toast.warn('Room already allocated!')
        } else if (res.data == 'SAVED') {
          toast.success('Room allocated successfully!')
          form.resetFields()
          setShowForm(false)
          setEditId(null)
          fetchAllocations()
        }
      }
    } catch {
      toast.error('Something went wrong')
    } finally {
      setLoading(false)
    }
  }

  // =============================
  // CANCEL BUTTON
  // =============================
  const handleCancel = () => {
    form.resetFields()
    setShowForm(false)
    setEditId(null)
  }

  // =============================
  // TABLE COLUMNS
  // =============================
  const columns = [
    {
      title: 'Sr No',
      render: (_, __, index) => (currentPage - 1) * pageSize + index + 1,
    },
    {
      title: 'Student Name',
      dataIndex: ['student', 'full_name'],
    },
    {
      title: 'Allocation Date',
      dataIndex: 'allocation_date',
    },
    {
      title: 'Building',
      dataIndex: ['building', 'building_name'],
    },
    {
      title: 'Wing',
      dataIndex: ['wing', 'wing_name'],
    },
    ...(showActionColumn
      ? [
          {
            title: 'Action',
            render: (_, record) => (
              <Space>
                {pagePermission.is_view == 1 && (
                  <Button
                    title="View"
                    icon={<EyeOutlined />}
                    onClick={() => handleView(record)}
                  />
                )}
                {pagePermission.is_edit == 1 && (
                  <Button
                    title="Edit"
                    icon={<EditOutlined />}
                    onClick={() => handleEdit(record)}
                  />
                )}
                {pagePermission.is_delete == 1 && (
                  <Button
                    title="Delete"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => {
                      setDeleteId(record.reg_id)
                      setDeleteModal(true)
                    }}
                  />
                )}
              </Space>
            ),
          },
        ]
      : []),
  ]

  return (
    <>
      <AdminHeader />

      <div className="student-form-section container-fluid">
        <div className="student-form-wrapper pb-5">
          <div className="row student-form-title">
            <h3 className="section-title1 text-start mb-3">Room Allocation</h3>
          </div>

          {/* ================= TABLE VIEW ================= */}
          {!showForm && (
            <>
              <Row justify="space-between" className="mb-3">
                <Col xs={24} md={14}></Col>
                <Col xs={24} md={4} className="text-end pb-2 px-md-2 pb-md-0">
                  {pagePermission.is_new == 1 && (
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={handleAdd}
                    >
                      Add
                    </Button>
                  )}
                </Col>
                <Col xs={24} md={6}>
                  <Input
                    placeholder="Search by name"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                  />
                </Col>
              </Row>

              <Table
                dataSource={allocationList.filter((item) => {
                  const search = searchText.toLowerCase()

                  return item.student?.full_name?.toLowerCase().includes(search)
                })}
                columns={columns}
                pagination={{
                  current: currentPage,
                  pageSize: pageSize,
                  onChange: (page, size) => {
                    setCurrentPage(page)
                    setPageSize(size)
                  },
                }}
                rowKey="id"
                bordered
                loading={tableLoading}
                className="shadow rounded custom-table p-0"
                style={{
                  overflowX: 'auto',
                  border: '1px solid #dfdfdf',
                  backgroundColor: 'white',
                }}
              />
            </>
          )}

          {/* ================= FORM VIEW ================= */}
          {showForm && (
            <Spin spinning={editLoading}>
              <div className="row student-form-container rounded shadow">
                <Form form={form} layout="vertical" onFinish={handleFinish}>
                  <Row
                    gutter={16}
                    className="mt-3 p-2 border rounded"
                    style={{ background: '#fafafa' }}
                  >
                    <Col xs={24} md={8}>
                      <Form.Item
                        label="Select Child"
                        name="childId"
                        rules={[{ required: true }]}
                      >
                        {/* <Select placeholder="Select Child">
                        {childrenList.map((child) => (
                          <Option key={child.id} value={child.id}>
                            {child.fname} {child.lname}
                          </Option>
                        ))}
                      </Select> */}

                        <Select
                          showSearch
                          placeholder="Search by name / reg no"
                          optionFilterProp="children"
                          filterOption={(input, option) =>
                            option?.children
                              ?.toString()
                              .toLowerCase()
                              .includes(input.toLowerCase())
                          }
                        >
                          {childrenList.map((child) => (
                            <Option key={child.id} value={child.id}>
                              {child.fname} {child.mname} {child.lname} (Reg No:{' '}
                              {child.reg_no})
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>

                    <Col xs={24} md={6}>
                      <Form.Item
                        label="Allocation Date"
                        name="allocationDate"
                        rules={[{ required: true }]}
                      >
                        <DatePicker style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>

                    <Col xs={24} md={5}>
                      <Form.Item
                        label="Building"
                        name="buildingId"
                        rules={[{ required: true }]}
                      >
                        <Select onChange={handleBuildingChange}>
                          {buildingList.map((b) => (
                            <Option key={b.id} value={b.id}>
                              {b.building_name}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>

                    <Col xs={24} md={5}>
                      <Form.Item
                        label="Wing"
                        name="wingId"
                        rules={[{ required: true }]}
                      >
                        <Select disabled={wingDisabled}>
                          {wingList.map((w) => (
                            <Option key={w.id} value={w.id}>
                              {w.wing_name}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Divider />

                  <Row justify="end" className="pt-3 pb-5">
                    <Space>
                      <Button onClick={handleCancel}>Cancel</Button>

                      <Button
                        type="primary"
                        htmlType="submit"
                        loading={loading}
                      >
                        {editId ? 'Update' : 'Submit'}
                      </Button>
                    </Space>
                  </Row>
                </Form>
              </div>
            </Spin>
          )}

          {/* ===== DELETE MODAL ===== */}
          <Modal
            title="Confirm Delete"
            open={deleteModal}
            onOk={handleDelete}
            onCancel={() => setDeleteModal(false)}
          >
            Are you sure you want to delete this record?
          </Modal>

          {/* ===== VIEW MODAL ===== */}
          <Modal
            title={
              <div style={{ fontSize: '18px', fontWeight: 600 }}>
                👁 View Room Allocation
              </div>
            }
            open={viewModalVisible}
            onCancel={() => setViewModalVisible(false)}
            footer={[
              <Button
                key="close"
                type="primary"
                onClick={() => setViewModalVisible(false)}
              >
                Close
              </Button>,
            ]}
            width={900}
            centered
          >
            {viewData && (
              <div
                style={{
                  backgroundColor: '#f5f7fa',
                  padding: '20px',
                  borderRadius: '8px',
                }}
              >
                {/* Student Name */}
                <Row className="mb-3">
                  <Col span={6}>
                    <strong>Student Name :</strong>
                  </Col>
                  <Col span={18}>
                    <div className="view-box">
                      {viewData?.student?.full_name || '-'}
                    </div>
                  </Col>
                </Row>

                {/* Allocation Date */}
                <Row className="mb-3">
                  <Col span={6}>
                    <strong>Allocation Date :</strong>
                  </Col>
                  <Col span={18}>
                    <div className="view-box">
                      {viewData?.allocation_date || '-'}
                    </div>
                  </Col>
                </Row>

                <Divider />

                {/* Building */}
                <Row className="mb-3">
                  <Col span={6}>
                    <strong>Building :</strong>
                  </Col>
                  <Col span={18}>
                    <div className="view-box">
                      {viewData?.building?.building_name || '-'}
                    </div>
                  </Col>
                </Row>

                {/* Wing */}
                <Row className="mb-3">
                  <Col span={6}>
                    <strong>Wing :</strong>
                  </Col>
                  <Col span={18}>
                    <div className="view-box">
                      {viewData?.wing?.wing_name || '-'}
                    </div>
                  </Col>
                </Row>
              </div>
            )}
          </Modal>
        </div>
      </div>

      <AdminFooter />
    </>
  )
}

export default RoomAllocationForm
